import { Info, TriangleAlert, Wallet } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { DataTable } from '@/components/data-table/data-table';
import { columnsFor } from '@/components/data-table/features';
import { EmptyState } from '@/components/empty-state';
import { SelectInput } from '@/components/form/select-input';
import { MetricCard, MetricGrid } from '@/components/metric-card';
import { MoneyValue } from '@/components/money-value';
import { PageHeader } from '@/components/page-header';
import { QueryState } from '@/components/query-state';
import { Page } from '@/components/shell/page';
import { PageToolbar } from '@/components/shell/page-toolbar';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge, type BadgeProps } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableFooter, TableRow } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  MISSING_LABELS,
  type RealizedMissing,
  useOrderPlatforms,
} from '@/features/orders/api/orders';
import { PlatformMark } from '@/features/orders/components/platform-mark';
import { formatMoney, formatPercent } from '@/lib/money';
import {
  type FinanceProduct,
  type MonthlyFinance,
  monthLabel,
  recentMonths,
  useMonthlyFinance,
} from '../api/finance';

const CONSOLIDATED = 'consolidado';

/**
 * Resultado do mês em regime de caixa (ADR-050, decisão do dono): conta pelo mês em que o dinheiro
 * **entrou**, não o da venda. Aba Consolidado (a empresa, com as despesas fixas) e uma por
 * plataforma (módulos §0) — despesa fixa é da empresa, então a aba de um canal para na margem de
 * contribuição.
 */
export function FinancePage() {
  const platforms = useOrderPlatforms();
  const [tab, setTab] = useState(CONSOLIDATED);
  const months = recentMonths();
  const [month, setMonth] = useState(months[0]!);
  const provider = tab === CONSOLIDATED ? undefined : tab;
  const report = useMonthlyFinance(month, provider ? { provider } : {});

  return (
    <Page size="xl">
      <PageHeader
        title="Resultado"
        description="Pelo mês em que o dinheiro entrou na conta (regime de caixa): o que as vendas deixaram de verdade."
      />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value={CONSOLIDATED}>Consolidado</TabsTrigger>
          {(platforms.data ?? []).map((platform) => (
            <TabsTrigger key={platform.provider} value={platform.provider} className="gap-2">
              <PlatformMark provider={platform.provider} />
              {platform.displayName}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      <PageToolbar>
        <SelectInput
          aria-label="Mês"
          className="w-auto min-w-48"
          value={month}
          options={months.map((value) => ({ value, label: monthLabel(value) }))}
          onValueChange={(next) => next && setMonth(next)}
        />
      </PageToolbar>
      <QueryState
        query={report}
        isEmpty={(data) => data.orders === 0 && !data.fixedExpenseLines?.length}
        empty={
          <EmptyState
            icon={Wallet}
            title="Nenhum dinheiro entrou neste mês"
            description="O mês conta pela data em que a plataforma libera o dinheiro de cada venda. Venda ainda não liberada está em A receber."
          />
        }
      >
        {(data) => <Report data={data} />}
      </QueryState>
    </Page>
  );
}

function Report({ data }: { data: MonthlyFinance }) {
  const missing = (Object.entries(data.completeness.missing) as [RealizedMissing, number][]).filter(
    ([, count]) => count > 0,
  );
  return (
    <div className="space-y-6">
      {data.releasePermissionMissing.length > 0 && (
        <Alert variant="destructive">
          <TriangleAlert aria-hidden />
          <AlertTitle>Falta a permissão de Faturamento</AlertTitle>
          <AlertDescription>
            {data.releasePermissionMissing.join(', ')}: sem ela, a plataforma não diz quando o
            dinheiro de cada venda entra, e essas vendas ficam fora do resultado. Reconecte a loja
            em <Link to="/integracoes">Integrações</Link> aceitando a permissão.
          </AlertDescription>
        </Alert>
      )}
      {data.releaseDatePending > 0 && (
        <Alert>
          <Info aria-hidden />
          <AlertTitle>Resultado ainda sendo completado</AlertTitle>
          <AlertDescription>
            {data.releaseDatePending} vendas entregues ainda estão com a data de liberação sendo
            consultada na plataforma e, por isso, ainda não aparecem no mês em que o dinheiro
            entrou.
          </AlertDescription>
        </Alert>
      )}
      {data.completeness.incompleteOrders > 0 && (
        <Alert>
          <Info aria-hidden />
          <AlertTitle>
            {data.completeness.incompleteOrders} de {data.orders} vendas com custo faltando
          </AlertTitle>
          <AlertDescription>
            Falta{' '}
            {missing
              .map(([code, count]) => `${MISSING_LABELS[code].toLowerCase()} em ${count}`)
              .join(', ')}
            . O que falta não vale zero: o resultado abaixo é o máximo possível — o real é esse ou
            menor.
          </AlertDescription>
        </Alert>
      )}

      <MetricGrid>
        <MetricCard
          label="Recebido bruto"
          value={formatMoney(data.cash.grossReceived)}
          hint={`${data.orders} vendas liberadas`}
        />
        <MetricCard
          label="Tarifa e frete da plataforma"
          value={formatMoney(data.cash.platformCosts)}
          {...(data.indicators.marketplaceCostRate
            ? { hint: `${formatPercent(data.indicators.marketplaceCostRate)} da receita` }
            : {})}
        />
        <MetricCard
          label="Recebido líquido"
          value={formatMoney(data.cash.netReceived)}
          hint="o que caiu na conta"
        />
        <MetricCard
          label="Resultado das vendas"
          value={formatMoney(data.cash.result)}
          {...(data.dre.contributionMarginRate
            ? { hint: `margem de contribuição ${formatPercent(data.dre.contributionMarginRate)}` }
            : {})}
        />
      </MetricGrid>

      <div className="grid gap-6 lg:grid-cols-2">
        <DreCard data={data} />
        <Card>
          <CardHeader>
            <CardTitle>Indicadores</CardTitle>
          </CardHeader>
          <CardContent>
            <MetricGrid className="lg:grid-cols-2">
              <MetricCard
                label="Ticket médio"
                value={<MoneyValue value={data.indicators.averageTicket} />}
              />
              <MetricCard
                label="Resultado por venda"
                value={<MoneyValue value={data.indicators.resultPerOrder} />}
              />
              <MetricCard
                label="Custo efetivo da plataforma"
                value={
                  data.indicators.marketplaceCostRate
                    ? formatPercent(data.indicators.marketplaceCostRate)
                    : '—'
                }
                hint="tarifa + frete ÷ receita"
              />
              {data.consolidated && (
                <MetricCard
                  label="Ponto de equilíbrio"
                  value={<MoneyValue value={data.dre.breakEven} />}
                  hint="receita no mês que cobre as despesas fixas"
                />
              )}
            </MetricGrid>
          </CardContent>
        </Card>
      </div>

      <ProductsCard products={data.products} />
    </div>
  );
}

/** DRE gerencial (não contábil), na ordem da decisão do dono (módulos §4). */
function DreCard({ data }: { data: MonthlyFinance }) {
  const dre = data.dre;
  const line = (label: string, value: string | null, indent = false) => (
    <TableRow key={label}>
      <TableCell className={indent ? 'pl-6 text-muted-foreground' : 'font-medium'}>
        {label}
      </TableCell>
      <TableCell className="text-right">
        <MoneyValue value={value} />
      </TableCell>
    </TableRow>
  );
  return (
    <Card>
      <CardHeader>
        <CardTitle>DRE gerencial</CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableBody>
            {line('Receita bruta', dre.grossRevenue)}
            {line('− Impostos', dre.taxes, true)}
            {line('Receita líquida', dre.netRevenue)}
            {line('− Custo da mercadoria', dre.productCost, true)}
            {line('− Tarifas da plataforma', dre.saleFees, true)}
            {line('− Frete pago', dre.shipping, true)}
            {line('− Embalagem', dre.packaging, true)}
            {dre.additional !== '0.00' && line('− Custos adicionais', dre.additional, true)}
            {dre.returns !== '0.00' && line('− Devoluções', dre.returns, true)}
            {line('Margem de contribuição', dre.contributionMargin)}
            {data.consolidated && (
              <>
                {(data.fixedExpenseLines ?? []).map((expense) =>
                  line(`− ${expense.name}`, expense.amount, true),
                )}
                {(data.fixedExpenseLines ?? []).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={2} className="pl-6 text-muted-foreground">
                      Nenhuma despesa fixa cadastrada para o mês —{' '}
                      <Link to="/financeiro/despesas">cadastrar</Link>
                    </TableCell>
                  </TableRow>
                )}
              </>
            )}
          </TableBody>
          {data.consolidated && (
            <TableFooter>
              <TableRow>
                <TableCell>Resultado operacional</TableCell>
                <TableCell className="text-right">
                  <MoneyValue value={dre.operatingResult} />
                </TableCell>
              </TableRow>
            </TableFooter>
          )}
        </Table>
        {!data.consolidated && (
          <p className="mt-3 text-xs text-muted-foreground">
            Despesa fixa é da empresa, não de uma plataforma: o resultado operacional está no
            Consolidado.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

const ABC_VARIANT: Record<'A' | 'B' | 'C', BadgeProps['variant']> = {
  A: 'success-soft',
  B: 'info-soft',
  C: 'neutral',
};

const productColumn = columnsFor<FinanceProduct>();

/**
 * Onde está o dinheiro: curva ABC por receita **e** por resultado, lado a lado. "A em receita, C em
 * resultado" é o produto que vende muito e rende pouco (módulos §4).
 */
function ProductsCard({ products }: { products: FinanceProduct[] }) {
  const columns = [
    productColumn.accessor('title', {
      header: 'Produto',
      meta: { className: 'whitespace-normal' },
      cell: ({ row }) => (
        <div>
          {row.original.productId ? (
            <Link to={`/produtos/${row.original.productId}`}>{row.original.title}</Link>
          ) : (
            row.original.title
          )}
          {row.original.sku && (
            <span className="block font-mono text-xs text-muted-foreground">
              {row.original.sku}
            </span>
          )}
        </div>
      ),
    }),
    productColumn.accessor('units', { header: 'Unid.', meta: { align: 'right' } }),
    productColumn.accessor('revenue', {
      sortFn: 'decimal',
      header: 'Receita',
      meta: { align: 'right' },
      cell: ({ row }) => <MoneyValue value={row.original.revenue} />,
    }),
    productColumn.accessor('result', {
      sortFn: 'decimal',
      header: 'Resultado',
      meta: { align: 'right' },
      cell: ({ row }) => <MoneyValue value={row.original.result} />,
    }),
    // Sem margem vai ao fim da lista: o `decimal` pede `undefined`, não nulo.
    productColumn.accessor((product) => product.margin ?? undefined, {
      id: 'margin',
      sortFn: 'decimal',
      header: 'Margem',
      meta: { align: 'right' },
      cell: ({ row }) => (row.original.margin ? formatPercent(row.original.margin) : '—'),
    }),
    productColumn.accessor('abcRevenue', {
      header: 'ABC receita',
      meta: { align: 'right' },
      cell: ({ row }) => (
        <Badge variant={ABC_VARIANT[row.original.abcRevenue]}>{row.original.abcRevenue}</Badge>
      ),
    }),
    productColumn.accessor('abcResult', {
      header: 'ABC resultado',
      meta: { align: 'right' },
      cell: ({ row }) => (
        <Badge variant={ABC_VARIANT[row.original.abcResult]}>{row.original.abcResult}</Badge>
      ),
    }),
  ];
  return (
    <Card>
      <CardHeader>
        <CardTitle>Produtos no mês</CardTitle>
      </CardHeader>
      <CardContent>
        {products.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nenhuma venda liberada no mês.</p>
        ) : (
          <DataTable
            data={products}
            columns={columns}
            getRowId={(product) => product.productId ?? product.sku ?? product.title}
            initialSorting={[{ id: 'revenue', desc: true }]}
          />
        )}
      </CardContent>
    </Card>
  );
}
